"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MoleculesDB = void 0;
const appendCSV_1 = __importDefault(require("./utils/appendCSV"));
const appendColor_1 = __importDefault(require("./utils/appendColor"));
const appendEntries_js_1 = __importDefault(require("./utils/appendEntries.js"));
const appendSDF_1 = __importDefault(require("./utils/appendSDF"));
const appendSmilesList_1 = __importDefault(require("./utils/appendSmilesList"));
const pushEntry_1 = __importDefault(require("./utils/pushEntry"));
const pushMoleculeInfo_1 = __importDefault(require("./utils/pushMoleculeInfo"));
const search_1 = require("./utils/search");
/*
    this.db is an object with properties 'oclID' that has as value
    an object that contains the following properties:
    * molecule: an OCL molecule instance
    * index: OCL index used for substructure searching
    * properties: all the calculates properties
    * data: array containing free data associated with this molecule
  */
class MoleculesDB {
    /**
     *
     * @param {import('openchemlib')} OCL - openchemlib library
     * @param {object} [options={}]
     * @param {boolean} [options.computeProperties=false]
     * @param {boolean} [options.keepEmptyMolecules=false]
     */
    constructor(OCL, options = {}) {
        const { computeProperties = false, keepEmptyMolecules = false } = options;
        this.OCL = OCL;
        this.db = {};
        this.statistics = null;
        this.computeProperties = computeProperties;
        this.keepEmptyMolecules = keepEmptyMolecules;
        this.searcher = new OCL.SSSearcherWithIndex();
    }
    /**
     * Append an array of entries to the current database. An entry is an object that by default should contain a 'ocl' property containing idCode and optionally index and coordinates
     * @param {*} moleculesDB
     * @param {object[]} entries
     * @param {object} [options={}]
     * @param {string} [options.idCodePath='ocl.idCode']
     * @param {string} [options.indexPath='ocl.index']
     * @param {string} [options.coordinatesPath='ocl.coordinates']
     * @param {string} [options.mwPath='mw']
     * @param {string} [options.smilesPath]
     * @param {string} [options.molfilePath]
     * @param {function} [options.onStep] call back to execute after each molecule
     */
    appendEntries(entries, options) {
        return (0, appendEntries_js_1.default)(this, entries, {
            computeProperties: this.computeProperties,
            ...options,
        });
    }
    /**
     * append to the current database a CSV file
     * @param {string|ArrayBuffer} csv - text file containing the comma separated value file
     * @param {object} [options={}]
     * @param {boolean} [options.header=true]
     * @param {boolean} [options.dynamicTyping=true]
     * @param {boolean} [options.skipEmptyLines=true]
     * @param {function} [options.onStep] call back to execute after each molecule
     */
    appendCSV(csv, options) {
        return (0, appendCSV_1.default)(this, csv, {
            computeProperties: this.computeProperties,
            ...options,
        });
    }
    /**
     * Append a SDF to the current database
     * @param {string|ArrayBuffer} sdf - text file containing the sdf
     * @param {object} [options={}]
     * @param {function} [options.onStep] call back to execute after each molecule
     * @returns {DB}
     */
    appendSDF(sdf, options) {
        return (0, appendSDF_1.default)(this, sdf, {
            computeProperties: this.computeProperties,
            ...options,
        });
    }
    /**
     * Append a SDF to the current database
     * @param {string|ArrayBuffer} smiles - text file containing a list of smiles
     * @param {object} [options={}]
     * @param {function} [options.onStep] call back to execute after each molecule
     * @returns {DB}
     */
    appendSmilesList(text, options) {
        return (0, appendSmilesList_1.default)(this, text, {
            computeProperties: this.computeProperties,
            ...options,
        });
    }
    /**
     * Add a molecule to the current database
     * @param {import('openchemlib').Molecule} molecule
     * @param {object} [data={}]
     * @param {object} [moleculeInfo={}] may contain precalculated index and mw
     */
    pushEntry(molecule, data, moleculeInfo) {
        (0, pushEntry_1.default)(this, molecule, data, moleculeInfo);
    }
    /**
     * Add an entry in the database
     * @param {object} moleculeInfo - a molecule as a JSON that may contain the following properties: molfile, smiles, idCode, mf, index
     * @param {object} [data={}]
     */
    pushMoleculeInfo(moleculeInfo, data) {
        return (0, pushMoleculeInfo_1.default)(this, moleculeInfo, data);
    }
    /**
     * Search in a MoleculesDB
     * Inside the database all the same molecules are group together
     * @param {string|OCL.Molecule} [query] smiles, molfile, idlCode or instance of Molecule to look for
     * @param {object} [options={}]
     * @param {'smiles'|'idCode'|'smarts'|'molfile'} [options.format='idCode'] - query format
     * @param {string} [options.mode='substructure'] - search by 'substructure', 'exact' or 'similarity'
     * @param {boolean} [options.flattenResult=true] - The database group the data for the same product. This allows to flatten the result
     * @param {boolean} [options.keepMolecule=false] - keep the OCL.Molecule object in the result
     * @param {number} [options.limit=Number.MAX_SAFE_INTEGER] - maximal number of result
     * @return {Array} array of object of the type {(molecule), idCode, data, properties}
     */
    search(query, options) {
        return (0, search_1.search)(this, query, options);
    }
    /**
     * Search in a MoleculesDB
     * Inside the database all the same molecules are group together
     * @param {string|OCL.Molecule} [query] smiles, molfile, idCode or instance of Molecule to look for
     * @param {object} [options={}]
     * @param {'smiles'|'idCode'|'smarts'|'molfile'} [options.format='idCode'] - query format
     * @param {string} [options.mode='substructure'] - search by 'substructure', 'exact' or 'similarity'
     * @param {boolean} [options.flattenResult=true] - The database group the data for the same product. This allows to flatten the result
     * @param {boolean} [options.keepMolecule=false] - keep the OCL.Molecule object in the result
     * @param {number} [options.limit=Number.MAX_SAFE_INTEGER] - maximal number of result
     * @param {number} [options.interval=100] - interval in ms to call the onStep callback
     * @param {function} [options.onStep] - callback to execute after each interval
     * @param {AbortController} [options.controler] - callback to execute to check if the search should be aborted
     * @return {Promise<Array>} array of object of the type {(molecule), idCode, data, properties}
     */
    searchAsync(query, options) {
        return (0, search_1.searchAsync)(this, query, options);
    }
    /**
     * Returns an array with the current database
     * @returns
     */
    getDB() {
        return Object.keys(this.db).map((key) => this.db[key]);
    }
    /**
     * Append the property `data.color` to each entry based on a data or property label
     * {object} [options={}]
     * {string} [options.dataLabel] name of the property from `data` to use
     * {string} [options.propertyLabel] name of the property from `properties` to use
     * {number} [options.colorLabel='color'] name of the property to add in data that will contain the color
     * {number} [options.minValue]
     * {number} [options.maxValue]
     * {number} [options.minHue=0]
     * {number} [options.maxHue=360]
     * {number} [options.saturation=65] percent of color saturation
     * {number} [options.lightness=65] percent of color lightness
     */
    appendColor(options) {
        (0, appendColor_1.default)(this, options);
    }
}
exports.MoleculesDB = MoleculesDB;
//# sourceMappingURL=MoleculesDB.js.map